import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { GoldButton } from '@/components/common/GoldButton';
import { useAuth } from '@/context/AuthContext';
import { useApiErrorToast } from '@/hooks/useApiErrorToast';
import { useToast } from '@/hooks/useToast';
import { t } from '@/i18n';
import {
  canonicalizeEmailForSubmit,
  getEmailValidationError,
  getNewPasswordError,
  isSupportedEmail,
  MAX_EMAIL_LENGTH,
  newPasswordsMatch,
} from '@/utils/authValidation';
import styles from './VerifyEmailPage.module.css';

type Phase = 'form' | 'submitting' | 'success';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { completePasswordReset } = useAuth();
  const onApiError = useApiErrorToast();
  const { showToast } = useToast();

  const token = searchParams.get('token') ?? '';
  const [phase, setPhase] = useState<Phase>('form');
  const [email, setEmail] = useState(searchParams.get('email') ?? '');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [touched, setTouched] = useState({
    email: false,
    code: false,
    password: false,
    confirm: false,
  });
  const [codeError, setCodeError] = useState(false);

  const passwordError = getNewPasswordError(newPassword);
  const lengthErr = touched.password && passwordError !== null;
  const matchErr = touched.confirm && !newPasswordsMatch(newPassword, confirm);

  useEffect(() => {
    if (phase === 'success') {
      const id = setTimeout(() => navigate('/login', { replace: true }), 2000);
      return () => clearTimeout(id);
    }
  }, [phase, navigate]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched({ email: true, code: true, password: true, confirm: true });
    if (
      (!token && (!isSupportedEmail(email) || !/^\d{6}$/.test(code))) ||
      getNewPasswordError(newPassword) !== null ||
      !newPasswordsMatch(newPassword, confirm) ||
      phase === 'submitting'
    )
      return;

    setPhase('submitting');
    setCodeError(false);
    try {
      await completePasswordReset(
        token
          ? { token, newPassword }
          : {
              email: canonicalizeEmailForSubmit(email),
              code,
              newPassword,
            },
      );
      showToast(t.auth.reset.successToast, 'default');
      setPhase('success');
    } catch (err) {
      if (!token) setCodeError(true);
      onApiError(
        err,
        token ? t.auth.reset.invalidLink : t.auth.reset.invalidCode,
      );
      setPhase('form');
    }
  };

  if (phase === 'success') {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles.icon}>✅</div>
          <div className={styles.title}>{t.auth.reset.successTitle}</div>
          <div className={styles.sub}>{t.auth.reset.successSub}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.icon}>🔑</div>
        <div className={styles.title}>{t.auth.reset.title}</div>
        <div className={styles.sub}>
          {token ? t.auth.reset.sub : t.auth.reset.codeSub}
        </div>
        <form onSubmit={submit} className={styles.resendForm} noValidate>
          {!token && (
            <>
              <input
                className={styles.inp}
                type="email"
                placeholder={t.common.emailPlaceholder}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                onBlur={() =>
                  setTouched((value) => ({ ...value, email: true }))
                }
                disabled={phase === 'submitting'}
                autoComplete="email"
                autoCapitalize="none"
                inputMode="email"
                spellCheck={false}
                maxLength={MAX_EMAIL_LENGTH}
                required
              />
              {touched.email && !isSupportedEmail(email) && (
                <div className={styles.fieldError}>
                  {getEmailValidationError(email) === 'internationalLocalPart'
                    ? t.common.emailInternationalUnsupported
                    : t.common.emailInvalid}
                </div>
              )}
              <input
                className={`${styles.inp} ${styles.codeInput}`}
                type="text"
                placeholder="000000"
                value={code}
                onChange={(event) => {
                  setCode(event.target.value.replace(/\D/g, '').slice(0, 6));
                  setCodeError(false);
                }}
                onBlur={() => setTouched((value) => ({ ...value, code: true }))}
                disabled={phase === 'submitting'}
                autoComplete="one-time-code"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                aria-label={t.auth.reset.codeLabel}
                required
                autoFocus
              />
              {((touched.code && code.length !== 6) || codeError) && (
                <div className={styles.fieldError}>
                  {codeError
                    ? t.auth.reset.invalidCode
                    : t.auth.verify.codeLength}
                </div>
              )}
            </>
          )}
          <input
            className={styles.inp}
            type="password"
            placeholder={t.auth.reset.newPasswordPlaceholder}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            onBlur={() => setTouched((value) => ({ ...value, password: true }))}
            disabled={phase === 'submitting'}
            autoComplete="new-password"
            required
          />
          {lengthErr && (
            <div style={{ fontSize: 11, color: '#e53e3e', marginTop: -6 }}>
              {passwordError === 'tooLong'
                ? t.common.passwordMax128
                : t.common.passwordMin8}
            </div>
          )}
          <input
            className={styles.inp}
            type="password"
            placeholder={t.auth.reset.confirmPlaceholder}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            onBlur={() => setTouched((value) => ({ ...value, confirm: true }))}
            disabled={phase === 'submitting'}
            autoComplete="new-password"
            required
          />
          {matchErr && (
            <div style={{ fontSize: 11, color: '#e53e3e', marginTop: -6 }}>
              {t.common.passwordsDoNotMatch}
            </div>
          )}
          <GoldButton type="submit" disabled={phase === 'submitting'}>
            {phase === 'submitting' ? t.common.saving : t.auth.reset.submit}
          </GoldButton>
        </form>
        {!token && (
          <Link to="/forgot-password" className={styles.backLink}>
            {t.auth.reset.requestNewCode}
          </Link>
        )}
        <Link to="/login" className={styles.backLink}>
          {t.common.backToLogin}
        </Link>
      </div>
    </div>
  );
}
